import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { IconPencil, IconPlus } from '@tabler/icons-react';

import type { UserListItemResponseDto } from '@shared/services/apiService';

import { isBuiltIn } from '../model/privileges';
import type { AccessControlApi } from '../model/useAccessControlApi';
import { GroupChip } from './GroupChip';
import { UserDialog } from './UserDialog';
import { useStyles } from './styles';

const ALL_GROUPS = '';

interface UsersPanelProps {
  api: AccessControlApi;
  /** Group id to show the members of; empty for everyone. */
  groupFilter: string;
  onGroupFilterChange: (groupId: string) => void;
}

export const UsersPanel: React.FC<UsersPanelProps> = ({
  api,
  groupFilter,
  onGroupFilterChange,
}) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const [search, setSearch] = useState('');
  // `null` is a new user; `undefined` is no dialog.
  const [editingId, setEditingId] = useState<string | null>();

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return api.users.filter(
      (user) =>
        (!groupFilter || user.groups.some((group) => group.id === groupFilter)) &&
        `${user.firstName} ${user.lastName} ${user.email}`.toLowerCase().includes(query),
    );
  }, [api.users, search, groupFilter]);

  // Looked up by id so the dialog follows the refetched user after a save.
  const editing: UserListItemResponseDto | null =
    api.users.find((user) => user.id === editingId) ?? null;
  const closeDialog = () => setEditingId(undefined);

  if (api.isUsersLoading) {
    return (
      <Box className={classes.loader}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Box className={classes.panelHeader}>
        <Box className={classes.filters}>
          <TextField
            size="small"
            placeholder={t('accessControl.users.search')}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ width: 320, maxWidth: '100%' }}
          />
          <TextField
            select
            size="small"
            value={groupFilter}
            onChange={(event) => onGroupFilterChange(event.target.value)}
            SelectProps={{ displayEmpty: true }}
            sx={{ minWidth: 200 }}
            data-testid="access-users-group-filter">
            <MenuItem value={ALL_GROUPS}>{t('accessControl.users.allGroups')}</MenuItem>
            {api.groups.map((group) => (
              <MenuItem key={group.id} value={group.id}>
                {group.name}
              </MenuItem>
            ))}
          </TextField>
        </Box>
        {api.canManageAccounts && (
          <Button
            variant="contained"
            startIcon={<IconPlus size={18} />}
            onClick={() => setEditingId(null)}
            className={classes.noWrap}
            data-testid="access-user-create">
            {t('accessControl.users.create')}
          </Button>
        )}
      </Box>

      <TableContainer component={Paper} className={classes.table}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('accessControl.users.columns.name')}</TableCell>
              <TableCell>{t('accessControl.users.columns.email')}</TableCell>
              <TableCell>{t('accessControl.users.columns.baseAccess')}</TableCell>
              <TableCell>{t('accessControl.users.columns.groups')}</TableCell>
              <TableCell align="right">{t('accessControl.groups.columns.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map((user) => {
              const isSelf = user.id === api.actorId;
              const baseGroups = user.groups.filter(isBuiltIn);
              const extraGroups = user.groups.filter((group) => !isBuiltIn(group));
              const isAdmin = baseGroups.some((group) => group.builtIn === 'admin');

              return (
                <TableRow key={user.id} hover data-testid={`access-user-row-${user.id}`}>
                  <TableCell>
                    <Box className={classes.nameCell}>
                      <Typography fontWeight={600} className={classes.noWrap}>
                        {user.firstName} {user.lastName}
                      </Typography>
                      {isSelf && (
                        <Chip size="small" color="primary" label={t('accessControl.users.you')} />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <Box className={classes.chips}>
                      {baseGroups.map((group) => (
                        <GroupChip key={group.id} group={group} />
                      ))}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {extraGroups.length > 0 ? (
                      <Box className={classes.chips}>
                        {extraGroups.map((group) => (
                          <GroupChip key={group.id} group={group} redundant={isAdmin} />
                        ))}
                      </Box>
                    ) : (
                      <Typography color="text.secondary">—</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box className={classes.actions}>
                      <Tooltip
                        title={t(
                          isSelf
                            ? 'accessControl.users.selfTooltip'
                            : api.canManageAccounts
                              ? 'accessControl.users.edit'
                              : 'accessControl.users.manageGroups',
                        )}>
                        {/* A disabled button fires no events, so the tooltip needs a wrapper. */}
                        <span>
                          <IconButton
                            size="small"
                            disabled={isSelf}
                            onClick={() => setEditingId(user.id)}
                            data-testid={`access-user-edit-${user.id}`}>
                            <IconPencil size={18} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className={classes.emptyState}>
                  {t('accessControl.users.empty')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <UserDialog open={editingId !== undefined} user={editing} api={api} onClose={closeDialog} />
    </>
  );
};
